import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faPlus,
  faEye,
  faBox,
  faBoxes,
  faList,
  faSave,
  faBoxOpen,
  faUsers,
  faUserCheck,
  faUserSlash,
  faSpinner,
  faToggleOn,
  faToggleOff,
  faCalendarPlus,
  faCalendarCheck,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import {
  statusStyles,
  statusDotStyles,
  formatDateTime,
} from "../../../utils/styles";
import Modal from "../../../components/ui/modal";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { Input } from "../../../components/ui/input";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import DateFilter from "../../../components/ui/DateFilter";
import { useAuth } from "../../../hooks";
import { toastSuccess, toastError } from "../../../utils/toast";

const DEFAULT_PACKAGE_TYPES = [
  "Box",
  "Carton",
  "Bundle",
  "Piece",
  "Bag",
  "Pallet",
  "Crate",
  "Other",
];

const SelectSearchBox = ({ value, onChange, placeholder }) => (
  <div className="sticky top-0 z-10 bg-white p-1.5 border-b border-gray-100">
    <div className="relative">
      <FontAwesomeIcon
        icon={faSearch}
        className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        placeholder={placeholder}
        className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800/30"
      />
    </div>
  </div>
);

const Product = ({
  products,
  productsLoading,
  productsError,
  productsPagination,
  productsPage,
  setProductsPage,
  productsPageSize,
  setProductsPageSize,
  productsSearch,
  setProductsSearch,
  productCategories,
  createProduct,
  updateProduct,
  toggleProductStatus,
  refreshProducts,
  getPackageTypes,
}) => {
  const { hasPermission } = useAuth();
  const canManageProduct = hasPermission(["manage_product"]);

  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products],
  );
  const safeProductCategories = useMemo(
    () => (Array.isArray(productCategories) ? productCategories : []),
    [productCategories],
  );

  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState({
    product_name: "",
    package_type: "",
    no_of_items: "1",
    product_category_id: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);

  const [packageTypeSearch, setPackageTypeSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");

  const [packageTypes, setPackageTypes] = useState(DEFAULT_PACKAGE_TYPES);
  const [packageTypesLoading, setPackageTypesLoading] = useState(false);
  const packageTypesLoaded = useRef(false);

  useEffect(() => {
    if (!dialogOpen || !getPackageTypes || packageTypesLoaded.current) return;
    let mounted = true;
    (async () => {
      try {
        setPackageTypesLoading(true);
        const response = await getPackageTypes();
        const raw = response?.data?.data ?? response?.data ?? [];
        const normalized = Array.isArray(raw)
          ? raw
              .map((item) =>
                typeof item === "string"
                  ? item
                  : item?.name || item?.package_type || null,
              )
              .filter(Boolean)
          : [];
        if (mounted) {
          setPackageTypes(
            normalized.length ? normalized : DEFAULT_PACKAGE_TYPES,
          );
          packageTypesLoaded.current = true;
        }
      } catch {
        if (mounted) setPackageTypes(DEFAULT_PACKAGE_TYPES);
      } finally {
        if (mounted) setPackageTypesLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [dialogOpen, getPackageTypes]);

  const getCategory = useCallback((item) => item?.product_category || null, []);
  const getCategoryName = useCallback(
    (item) =>
      getCategory(item)?.category_name || getCategory(item)?.name || "-",
    [getCategory],
  );
  const getTypeName = useCallback(
    (item) => getCategory(item)?.product_type?.name || "-",
    [getCategory],
  );
  const getCategoryId = useCallback(
    (item) =>
      getCategory(item)?.productCategoryId ?? getCategory(item)?.id ?? "",
    [getCategory],
  );

  const activeCategories = useMemo(
    () => safeProductCategories.filter((c) => c.status === "active"),
    [safeProductCategories],
  );

  const filteredCategoryOptions = useMemo(() => {
    if (!categorySearch.trim()) return activeCategories;
    const term = categorySearch.toLowerCase();
    return activeCategories.filter((c) => {
      const catName = (c.category_name || c.name || "").toLowerCase();
      const typeName = (c.product_type?.name || "").toLowerCase();
      return catName.includes(term) || typeName.includes(term);
    });
  }, [activeCategories, categorySearch]);

  const filteredPackageTypeOptions = useMemo(() => {
    if (!packageTypeSearch.trim()) return packageTypes;
    const term = packageTypeSearch.toLowerCase();
    return packageTypes.filter((pt) => pt.toLowerCase().includes(term));
  }, [packageTypes, packageTypeSearch]);

  useEffect(() => {
    if (dialogOpen) {
      if (editingProduct) {
        setForm({
          product_name: editingProduct.product_name || "",
          package_type: editingProduct.package_type || "",
          no_of_items: String(editingProduct.no_of_items ?? "1"),
          product_category_id: String(getCategoryId(editingProduct) || ""),
        });
      } else {
        setForm({
          product_name: "",
          package_type: "",
          no_of_items: "1",
          product_category_id: "",
        });
      }
      setErrors({});
      setPackageTypeSearch("");
      setCategorySearch("");
    }
  }, [dialogOpen, editingProduct, getCategoryId]);

  const filteredProducts = useMemo(() => {
    let result = safeProducts;
    if (statusFilter !== "all") {
      result = result.filter(
        (product) => product.status?.toLowerCase() === statusFilter,
      );
    }
    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((p) => {
        const createdDate = new Date(p.created_at);
        return createdDate >= start && createdDate <= end;
      });
    }
    return result;
  }, [safeProducts, statusFilter, startDate, endDate, isFilterApplied]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave = async () => {
    if (!canManageProduct) return;
    const newErrors = {};
    if (!form.product_name.trim() || form.product_name.trim().length < 3)
      newErrors.product_name = "Product name must be at least 3 characters";
    if (!form.package_type) newErrors.package_type = "Package type is required";
    if (form.no_of_items === "" || parseInt(form.no_of_items, 10) < 0)
      newErrors.no_of_items = "Valid number of items is required";
    if (!form.product_category_id)
      newErrors.product_category_id = "Product category is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    try {
      setIsSubmitting(true);
      const data = {
        product_name: form.product_name,
        package_type: form.package_type,
        no_of_items: parseInt(form.no_of_items, 10),
        product_category_id: form.product_category_id,
        status: editingProduct ? editingProduct.status || "active" : "active",
      };
      if (editingProduct) {
        await updateProduct(editingProduct.productId, data);
        toastSuccess("Product updated successfully!");
      } else {
        await createProduct(data);
        toastSuccess("Product created successfully!");
      }
      setDialogOpen(false);
      setEditingProduct(null);
      setErrors({});
      await refreshProducts();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to save product");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (productId, currentStatus) => {
    if (!canManageProduct) return;
    try {
      await toggleProductStatus(productId);
      toastSuccess(
        `Product ${currentStatus === "active" ? "deactivated" : "activated"} successfully!`,
      );
      await refreshProducts();
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to update status");
    }
  };

  const statusTabs = [
    { id: "all", label: "All", icon: faUsers },
    { id: "active", label: "Active", icon: faUserCheck },
    { id: "inactive", label: "Inactive", icon: faUserSlash },
  ];

  const tabCounts = {
    all: productsPagination?.total ?? safeProducts.length,
    active: safeProducts.filter((p) => p.status === "active").length,
    inactive: safeProducts.filter((p) => p.status === "inactive").length,
  };

  const totalItems = productsPagination?.total ?? safeProducts.length;
  const totalPages = productsPagination?.last_page ?? 1;

  if (productsLoading && safeProducts.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-8 w-8 text-blue-600 animate-spin"
          />
          <p className="text-gray-600 text-sm">Loading products...</p>
        </div>
      </div>
    );
  }

  if (productsError) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-600">
          Failed to load products. Please try again.
        </p>
        <Button
          onClick={() => refreshProducts()}
          className="ml-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Products</h3>
          <p className="text-sm text-gray-600">
            Manage your product inventory ({totalItems} total)
          </p>
        </div>
        {canManageProduct && (
          <Button
            onClick={() => {
              setEditingProduct(null);
              setDialogOpen(true);
            }}
            className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Product
          </Button>
        )}
      </div>

      <DateFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={() => {
          setIsFilterApplied(true);
          setProductsPage(1);
        }}
        onClear={() => {
          setStartDate("");
          setEndDate("");
          setIsFilterApplied(false);
          setProductsPage(1);
        }}
        isFilterApplied={isFilterApplied}
        totalItems={filteredProducts.length}
        label="products"
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={productsSearch}
          onChange={(value) => {
            setProductsSearch(value);
            setProductsPage(1);
          }}
          placeholder="Search by product name, package type, product type or category..."
          className="flex-1"
        />

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {statusTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setStatusFilter(tab.id);
                setProductsPage(1);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                statusFilter === tab.id
                  ? "bg-blue-800 text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="h-3 w-3" />
              <span>{tab.label}</span>
              <span
                className={`ml-0.5 px-1.5 py-0.5 text-xs rounded-full ${
                  statusFilter === tab.id
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {tabCounts[tab.id]}
              </span>
            </button>
          ))}
        </div>
      </div>

      <TableWrapper
        title="items"
        totalItems={totalItems}
        currentPage={productsPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                S/N
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Product Name
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Package Type
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Items
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Product Type
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Category
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4">
                Status
              </TableHead>
              <TableHead className="text-xs font-semibold text-gray-600 uppercase tracking-wider py-3 px-4 text-center">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.length > 0 ? (
              filteredProducts.map((item, i) => {
                const status = item.status || "active";
                const rowNumber = (productsPage - 1) * productsPageSize + i + 1;
                return (
                  <TableRow
                    key={item.productId || i}
                    className="hover:bg-blue-50/50 transition-colors border-b border-gray-100"
                  >
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {rowNumber}
                    </TableCell>
                    <TableCell className="py-3 px-4 font-medium text-gray-900 text-sm">
                      {item.product_name}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      <Badge
                        variant="outline"
                        className="bg-blue-50 text-blue-700 border-blue-200 font-normal px-3 py-1 rounded-full text-xs"
                      >
                        {item.package_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600 text-center">
                      {item.no_of_items}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      {getTypeName(item)}
                    </TableCell>
                    <TableCell className="py-3 px-4 text-sm text-gray-600">
                      <Badge
                        variant="outline"
                        className="bg-purple-50 text-purple-700 border-purple-200 font-normal px-3 py-1 rounded-full text-xs"
                      >
                        {getCategoryName(item)}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <Badge
                        variant="outline"
                        className={`${statusStyles[status.toUpperCase()] || "bg-gray-100 text-gray-700"} border font-normal px-3 py-1 rounded-full text-xs flex items-center gap-1.5`}
                      >
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${statusDotStyles[status.toUpperCase()] || "bg-gray-400"}`}
                        />
                        {status.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 px-4">
                      <div className="flex gap-2 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setViewProduct(item);
                            setViewModalOpen(true);
                          }}
                          className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50"
                        >
                          <FontAwesomeIcon icon={faEye} className="h-3 w-3" />{" "}
                          View
                        </Button>
                        {canManageProduct && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingProduct(item);
                                setDialogOpen(true);
                              }}
                              className="gap-1 border-gray-300 hover:border-blue-800 hover:bg-blue-50"
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="h-3 w-3"
                              />{" "}
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleToggleStatus(item.productId, status)
                              }
                              className={`gap-1 ${
                                status === "active"
                                  ? "border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                                  : "border-green-500 text-green-600 hover:bg-green-50"
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={
                                  status === "active" ? faToggleOff : faToggleOn
                                }
                                className="h-3 w-3"
                              />
                              {status === "active" ? "Deactivate" : "Activate"}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <EmptyState
                    title="No products found"
                    message={
                      productsSearch || isFilterApplied
                        ? "Try adjusting your search or filters"
                        : "Add your first product to get started"
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {totalItems > 0 && (
        <Pagination
          currentPage={productsPage}
          totalPages={totalPages}
          pageSize={productsPageSize}
          totalItems={totalItems}
          onPageChange={setProductsPage}
          onPageSizeChange={setProductsPageSize}
          pageSizeOptions={[5, 10, 25, 50, 100]}
        />
      )}

      <Modal
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingProduct(null);
          setErrors({});
        }}
        title={editingProduct ? "Edit product" : "Add new product"}
        maxWidth="sm:max-w-lg"
        footer={
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                setEditingProduct(null);
                setErrors({});
              }}
              disabled={isSubmitting}
              className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSubmitting}
              className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="h-4 w-4 animate-spin"
                  />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  {editingProduct ? "Update" : "Save"}
                </>
              )}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon icon={faBox} className="text-blue-800 h-4 w-4" />
            </div>
            <div>
              <p className="text-xs text-gray-600 font-semibold">
                {editingProduct ? "Edit product" : "Create new product"}
              </p>
              <p className="text-xs text-gray-600">
                Fill in all required fields (*)
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faBox}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Product name *
            </label>
            <Input
              name="product_name"
              value={form.product_name}
              onChange={handleChange}
              placeholder="Enter product name"
              disabled={isSubmitting}
              className={`border ${errors.product_name ? "border-red-500" : "border-gray-300"} rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
            />
            {errors.product_name && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.product_name}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-gray-700 font-medium flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faBoxOpen}
                  className="text-blue-800 h-3.5 w-3.5"
                />
                Package type *
              </label>
              <Select
                value={form.package_type}
                onValueChange={(value) => {
                  setForm((prev) => ({ ...prev, package_type: value }));
                  if (errors.package_type)
                    setErrors((prev) => ({ ...prev, package_type: "" }));
                }}
                disabled={isSubmitting || packageTypesLoading}
              >
                <SelectTrigger
                  className={`w-full h-10 ${errors.package_type ? "border-red-500" : "border-gray-300"}`}
                >
                  <SelectValue
                    placeholder={
                      packageTypesLoading ? "Loading..." : "Select package type"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectSearchBox
                    value={packageTypeSearch}
                    onChange={setPackageTypeSearch}
                    placeholder="Search package types..."
                  />
                  {filteredPackageTypeOptions.length > 0 ? (
                    filteredPackageTypeOptions.map((pt) => (
                      <SelectItem key={pt} value={pt}>
                        {pt}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      No matches
                    </div>
                  )}
                </SelectContent>
              </Select>
              {errors.package_type && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.package_type}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-gray-700 font-medium flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faBoxes}
                  className="text-blue-800 h-3.5 w-3.5"
                />
                No of items *
              </label>
              <Input
                name="no_of_items"
                type="number"
                min="0"
                value={form.no_of_items}
                onChange={handleChange}
                placeholder="0"
                disabled={isSubmitting}
                className={`border ${errors.no_of_items ? "border-red-500" : "border-gray-300"} rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
              />
              {errors.no_of_items && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                  {errors.no_of_items}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-medium flex items-center gap-2">
              <FontAwesomeIcon
                icon={faList}
                className="text-blue-800 h-3.5 w-3.5"
              />
              Category *
            </label>
            <Select
              value={form.product_category_id}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, product_category_id: value }));
                if (errors.product_category_id) {
                  setErrors((prev) => ({ ...prev, product_category_id: "" }));
                }
              }}
              disabled={isSubmitting}
            >
              <SelectTrigger
                className={`w-full h-10 ${errors.product_category_id ? "border-red-500" : "border-gray-300"}`}
              >
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectSearchBox
                  value={categorySearch}
                  onChange={setCategorySearch}
                  placeholder="Search categories or types..."
                />
                {filteredCategoryOptions.length > 0 ? (
                  filteredCategoryOptions.map((c) => (
                    <SelectItem
                      key={c.productCategoryId}
                      value={String(c.productCategoryId)}
                    >
                      {c.category_name || c.name}
                      {c.product_type?.name ? ` — ${c.product_type.name}` : ""}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No active categories found
                  </div>
                )}
              </SelectContent>
            </Select>
            {errors.product_category_id && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                {errors.product_category_id}
              </p>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewProduct(null);
        }}
        title="Product details"
        maxWidth="sm:max-w-2xl"
        footer={
          <div className="flex flex-col sm:flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setViewModalOpen(false);
                setViewProduct(null);
              }}
              className="px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none transition-all duration-200"
            >
              Close
            </Button>
            {canManageProduct && (
              <Button
                onClick={() => {
                  if (viewProduct) {
                    setEditingProduct(viewProduct);
                    setViewModalOpen(false);
                    setDialogOpen(true);
                  }
                }}
                className="gap-2 px-6 bg-blue-800 hover:bg-blue-900 text-white transition-all duration-200"
              >
                <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
                Edit product
              </Button>
            )}
          </div>
        }
      >
        {viewProduct && (
          <div className="flex flex-col gap-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-800 to-blue-600 flex items-center justify-center shadow-lg">
                  <FontAwesomeIcon
                    icon={faBox}
                    className="text-white h-6 w-6"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {viewProduct.product_name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge
                      variant="outline"
                      className="bg-blue-50 text-blue-700 border-blue-200"
                    >
                      <FontAwesomeIcon
                        icon={faBoxOpen}
                        className="mr-1.5 h-3 w-3"
                      />
                      {viewProduct.package_type}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={
                        statusStyles[(viewProduct.status || "").toUpperCase()]
                      }
                    >
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${statusDotStyles[(viewProduct.status || "").toUpperCase()]} mr-1.5`}
                      />
                      {(viewProduct.status || "").toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-800">No of items</p>
                <Badge
                  variant="outline"
                  className="rounded-full bg-blue-800 px-3 text-md text-white"
                >
                  {viewProduct.no_of_items}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white p-4 rounded-lg border-2 border-blue-600">
                <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">
                  Package Type
                </p>
                <p className="text-base font-bold text-black mt-1">
                  {viewProduct.package_type}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border-2 border-purple-600">
                <p className="text-xs text-purple-600 font-medium uppercase tracking-wider">
                  Product Type
                </p>
                <p className="text-sm font-bold text-black mt-1 truncate">
                  {getTypeName(viewProduct)}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border-2 border-orange-600">
                <p className="text-xs text-orange-600 font-medium uppercase tracking-wider">
                  Category
                </p>
                <p className="text-sm font-bold text-black mt-1 truncate">
                  {getCategoryName(viewProduct)}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border-2 border-green-600">
                <p className="text-xs text-green-600 font-medium uppercase tracking-wider">
                  Status
                </p>
                <p className="text-sm font-bold text-black mt-1 truncate">
                  {(viewProduct.status || "").toUpperCase()}
                </p>
              </div>
            </div>

            <div className="bg-gray-50/80 rounded-lg border border-gray-200 p-5">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faEye}
                  className="text-blue-800 h-4 w-4"
                />
                Detailed Information
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                    <FontAwesomeIcon
                      icon={faCalendarPlus}
                      className="text-blue-600 h-3.5 w-3.5"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-orange-600 font-medium">
                      Created At
                    </p>
                    <p className="text-sm font-semibold text-gray-800">
                      {formatDateTime(viewProduct.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center shrink-0 mt-0.5">
                    <FontAwesomeIcon
                      icon={faCalendarCheck}
                      className="text-purple-600 h-3.5 w-3.5"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-red-500 font-medium">
                      Updated At
                    </p>
                    <p className="text-sm font-semibold text-gray-800">
                      {formatDateTime(viewProduct.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Product;
